import { useState, useEffect, forwardRef } from "react";
import { Input, type InputProps } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export interface DecimalInputProps extends Omit<InputProps, "onChange" | "value" | "defaultValue"> {
  value?: number;
  defaultValue?: number;
  onChange?: (value: number) => void;
  onValueChange?: (value: number) => void;
  decimalPlaces?: number;
  locale?: string;
  allowNegative?: boolean;
}

export const DecimalInput = forwardRef<HTMLInputElement, DecimalInputProps>(
  (
    {
      value,
      defaultValue,
      onChange,
      onValueChange,
      className,
      decimalPlaces = 0,
      locale = "pt-BR",
      allowNegative = false,
      onBlur,
      ...rest
    },
    ref
  ) => {
    const [displayValue, setDisplayValue] = useState<string>("");

    const formatNumber = (num: number) => {
      const formatted = new Intl.NumberFormat(locale, {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
      }).format(num);
      // Substitui o separador de milhares (ponto) por espaço
      return formatted.replace(/\./g, " ");
    };

    useEffect(() => {
      const initialValue = value ?? defaultValue;
      if (typeof initialValue === "number" && !isNaN(initialValue)) {
        if (initialValue === 0) {
          setDisplayValue("");
        } else {
          setDisplayValue(formatNumber(initialValue));
        }
      } else {
        setDisplayValue("");
      }
    }, [value, defaultValue, locale, decimalPlaces]);

    const handleChange = (raw: string) => {
      const cleaned = raw.replace(/[\s]/g, "");
      const sign = allowNegative && cleaned.startsWith("-") ? -1 : 1;
      const digits = cleaned.replace(/[^0-9]/g, "");

      if (!digits) {
        setDisplayValue("");
        onChange?.(0);
        onValueChange?.(0);
        return;
      }

      // Para números inteiros (decimalPlaces = 0), não divide
      // Para decimais, divide por 10^decimalPlaces
      let numericValue: number;
      if (decimalPlaces === 0) {
        numericValue = parseInt(digits, 10) * sign;
      } else {
        const divisor = Math.pow(10, decimalPlaces);
        numericValue = (parseInt(digits, 10) / divisor) * sign;
      }
      
      const formatted = formatNumber(numericValue);

      setDisplayValue(formatted);
      onChange?.(numericValue);
      onValueChange?.(numericValue);
    };

    return (
      <Input
        ref={ref}
        inputMode="numeric"
        className={className}
        value={displayValue}
        onChange={(event) => handleChange(event.target.value)}
        onBlur={(event) => {
          if (!event.target.value) {
            setDisplayValue("");
          }
          onBlur?.(event);
        }}
        {...rest}
      />
    );
  }
);

DecimalInput.displayName = "DecimalInput";

